import React, { memo, useState, ChangeEvent, useEffect } from 'react'
import { DatePicker } from 'components/forms/DatePicker'
import moment from 'moment-timezone'
import { IFacilityAppointment } from 'types'
import { ITimeDate } from 'apps/detention-review/utils/types'
import { useUserContext } from 'apps/fleet-planner/context/UserContext'
import { ITimeFormats } from 'apps/fleet-planner/utils/userDateTimeSettings'
import { TimeInput } from 'transfix-ui'
import { formatDetentionTime } from 'utils/accessorialHelpers'
import { FORMATS } from 'constants/datetime'

interface IDateTimeInputsProps {
  appointment?: IFacilityAppointment
  label: string
  /**
   * @example '2022-12-30T02:30:00.000000Z'
   */
  timeStamp: string
  onChange: ({ date, time }: ITimeDate, id: string) => void
  timezone: string
  id: string
}

const getDefaultDate = (timeStamp: string, timezone: string): string => {
  const { date } = formatDetentionTime(timeStamp, timezone)
  return date
}

const getFormattedTime = (time: string, timezone: string, timeFormats: ITimeFormats): string => {
  return moment(time).tz(timezone).format(timeFormats.stopTimeInput)
}

export const DateTimeInputs = memo<IDateTimeInputsProps>(
  ({ appointment, id: inputId, label, onChange, timeStamp, timezone }) => {
    const [date, setDate] = useState<string>(getDefaultDate(timeStamp, timezone))
    const { timeFormats } = useUserContext()
    const { startAt = '' } = appointment || {}
    const timePlaceholder = getFormattedTime(startAt, timezone, timeFormats)
    const [time, setTime] = useState<string>(timePlaceholder)

    const dateChangeHandler = (date: string[]) => {
      if (date[0] !== '') {
        const newDate = moment(date[0]).format(FORMATS.dateFormat)
        setDate(newDate)
        onChange({ date: newDate, time }, inputId)
      }
    }

    const timeChangeHandler = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      setTime(value)
      onChange({ date, time: value }, inputId)
    }

    // Update form value to placeholder when the calculatede placeholder time changes
    useEffect(() => {
      setTime(timePlaceholder)
      onChange({ date, time: timePlaceholder }, inputId)
    }, [timePlaceholder])

    return (
      <div className='row pv2'>
        <div className='col-md-6 pl0'>
          <label className='label' htmlFor='description'>
            {label} Date
          </label>
          <DatePicker
            date={date}
            onDateChange={dateChangeHandler}
            id={`${inputId}-date`}
            placeholder={label}
          />
        </div>
        <div className='col-md-6'>
          <TimeInput
            id={`${label}-time`}
            timeFormat={timeFormats.stopTimeInput}
            value={time}
            label={`${label} Time`}
            placeholder={timePlaceholder}
            onChange={timeChangeHandler}
            dataTest={`${inputId}-time`}
            showSuggestionDropdown={false}
          />
        </div>
      </div>
    )
  }
)
